import {createAction, createAsyncThunk, PrepareAction} from '@reduxjs/toolkit'

import {eq, isEmpty, isNil} from '@shared/helpers/lang'

import {FDSPhase, FDSPhaseType} from '@web/core/constants'
import getStorage, {removeStorage} from '@web/helpers/storage'
import {currentFdsStatusSelector, FdsState} from '@web/slices/fds/slice'

export type RootState = {
    fds: FdsState
}

const FDS_STATE = '-_fds_stage_-'

export const storage = getStorage<{
    sheetKey: string
    fdsState: FdsState
}>(FDS_STATE)

type SaveFDSStateParam = {
    currentSheetKey: string
    fdsState: FdsState
}

/** FDS Authentication 상태를 저장합니다 */
export const saveFDSState = createAction<PrepareAction<FdsState>>(
    'processFds/saveFDSState',
    ({currentSheetKey, fdsState}: SaveFDSStateParam) => {
        storage.remove()
        storage.set({sheetKey: currentSheetKey, fdsState})

        return {
            payload: fdsState,
        }
    },
)

type RestoreFDSStateParam = {
    currentSheetKey: string
    sessionKey?: string
}

export const checkFdsStateExistence = createAction(
    'processFds/checkFdsStateExistence',
    ({currentSheetKey}: RestoreFDSStateParam) => {
        const saved = storage.get({})
        const savedStateExists = !isEmpty(saved)
        const currentSheetKeyStateExists = savedStateExists && eq(saved.sheetKey, currentSheetKey)

        return {
            payload: currentSheetKeyStateExists,
        }
    },
)

/**
 * FDS Authentication 페이지에서 되돌아온 경우 fds state를 복구합니다
 * 세션키를 전달받았다면, 복구한 데이터에 전달받은 세션키를 덮어씌웁니다. (ars 인증의 경우 인증페이지에서 받아온 세션키를 덮어씌워야 합니다.)
 * */
export const restoreFdsStateAfterAuth = createAction<PrepareAction<Maybe<FdsState>>>(
    'processFds/restoreFdsStateAfterAuth',
    ({currentSheetKey, sessionKey}: RestoreFDSStateParam) => {
        const saved = storage.get({})
        const savedStateExists = !isEmpty(saved)
        const currentSheetKeyStateExists = savedStateExists && eq(saved.sheetKey, currentSheetKey)

        if (sessionKey && !isNil(saved.fdsState.authentication)) {
            saved.fdsState.authentication.sessionKey = sessionKey
        }

        storage.remove()

        return {payload: currentSheetKeyStateExists ? saved.fdsState : null}
    },
)

/** 전달받은 FDS state 데이터를 slice에 알려 state를 복구합니다 */
export const restoreFdsState = createAction<FdsState, string>('processFds/restoreFdsState')

/** 전달받은 FDS state 키에 매핑된 데이터와 slice를 함께 초기화 한다 */
export const initializeFdsState = createAction<PrepareAction<FDSPhaseType>>(
    'processFds/initializeFdsState',
    ({currentSheetKey}: RestoreFDSStateParam) => {
        try {
            const saved = storage.get({})
            const savedStateExists = !isEmpty(saved)
            const currentSheetKeyStateExists = savedStateExists && eq(saved.sheetKey, currentSheetKey)

            currentSheetKeyStateExists && storage.remove()
        } catch {
            // 복구를 위해 접근할 수 없으면 initializing으로 되돌림
            return {payload: FDSPhase.INITIALIZING}
        }

        return {payload: FDSPhase.IDLE}
    },
)

/**
 * FDS sessionStorage를 초기화 한다.
 * FDS 상태가 IDLE이거나 FINISH 면 FDS process가 한 사이클 완료된 것이므로 sessionStorage를 비워준다
 * rejectValue는 임시
 */
export const clearFdsStorage = createAsyncThunk<void, void, {state: {fds: FdsState}; rejectValue: string}>(
    'processFds/clearFdsStorage',
    (_, {getState, rejectWithValue}): void | ReturnType<typeof rejectWithValue> => {
        const state = getState()
        const {done, isIdle} = currentFdsStatusSelector(state)

        if (done || isIdle) {
            removeStorage(FDS_STATE)
        } else {
            return rejectWithValue('fds session storage를 삭제 실패')
        }
    },
)
